import { useState, useEffect, useCallback } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField } from "@mui/material";
import apiService from "../../service/apiService";
import { FaRegEdit, FaEye, FaTrash } from "react-icons/fa";
import { Pagination } from "react-bootstrap";
import '../../assets/styles/cropmanage.css';
import { debounce } from "lodash";

const Seedvariety = () => {
  // Essential state variables only
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [newState, setNewState] = useState({ varietyName: "", description: "" });
  const [varietyname, setVarietyName] = useState("");
  const [alldata, setAllData] = useState([]);
  const [totalItems, setTotalitems] = useState(0);
  const [totalpage, setTotalpage] = useState(0);
  const [deletedata, setDeleteData] = useState([]);
  const [openeditmodal, setOpenEditmodal] = useState(false);

  const fetchData = async () => {
    try {
      const response = await apiService.get("/seed-varieties/get-all-paginated", {
        params: {
          pageNumber: page,
          pageSize: pageSize
        },
      });

      const body = response.data.body;
      const data = body.data;

      // Filter data based on search
      const filteredData = data.filter(item => 
        item.varietyName.toLowerCase().includes(varietyname.toLowerCase())
      );

      setAllData(filteredData);

      const formattedData = filteredData.map((item) => ({
        id: item.seedVarietyId,
        varietyName: item.varietyName,
        description: item.description || "N/A",
        seedVarietyId: item.seedVarietyId,
      }));

      setRows(formattedData);
      setTotalitems(filteredData.length);
      setTotalpage(Math.ceil(filteredData.length / pageSize));
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
      setTotalitems(0);
      setTotalpage(0);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), [
    varietyname, page, pageSize
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const handleEdit = (data) => {
    setSelectedRow(data);
    setOpenEdit(true);
  };

  const handleView = (data) => {
    setSelectedRow(data);
    setOpen(true);
  };

  const handleDeleteClick = (data) => {
    setDeleteData(data);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setNewState({ varietyName: "", description: "" });
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleConfEdit = () => {
    setOpenEditmodal(true);
  };

  const handleSaveEdit = async () => {
    try {
      if (!selectedRow.varietyName.trim()) {
        alert("Variety Name should not be empty.");
        return;
      }

      const { seedVarietyId } = selectedRow;
      const payload = {
        varietyName: selectedRow.varietyName,
        description: selectedRow.description,
      };

      const response = await apiService.put(`/seed-varieties/update/${seedVarietyId}`, payload);
      console.log("Update response:", response.data);
      fetchData();
    } catch (error) {
      console.error("Error updating data:", error);
    } finally {
      setOpenEdit(false);
      setOpenEditmodal(false);
    }
  };

  const handleSaveAdd = async () => {
    try {
      if (!newState.varietyName.trim()) {
        alert("Variety Name should not be empty.");
        return;
      }

      const payload = {
        varietyName: newState.varietyName,
        description: newState.description,
      };

      const response = await apiService.post("/seed-varieties/create", payload);
      console.log("Create response:", response.data);
      fetchData();
    } catch (error) {
      console.error("Error creating seed variety:", error);
    } finally {
      handleCloseAdd();
    }
  };

  const handleDelete = async () => {
    try {
      const seedVarietyId = deletedata.seedVarietyId;
      await apiService.delete(`/seed-varieties/delete/${seedVarietyId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting data:", error);
    } finally {
      setOpenDelete(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const paginationItems = [];
    const maxPagesToShow = 7;
    const startPages = 3;
    const endPages = 2;

    if (totalpage <= maxPagesToShow) {
      for (let pages = 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    } else {
      for (let pages = 1; pages <= startPages; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      if (page > startPages + 1) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      const middlePages = [];
      const startMiddlePage = Math.max(page - 1, startPages + 1);
      const endMiddlePage = Math.min(page + 1, totalpage - endPages);

      for (let pages = startMiddlePage; pages <= endMiddlePage; pages++) {
        middlePages.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      paginationItems.push(...middlePages);

      if (page < totalpage - endPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      for (let pages = totalpage - endPages + 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    }
    return paginationItems;
  };

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  // Get current page data for display
  const currentPageData = alldata.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Box m="2px">
      <div style={{ maxHeight: "100vh", overflowY: "auto" }}>
        {/* Header - Same design as distdata */}
        <div style={{
          backgroundColor: "#ffffff",
          borderBottom: "3px solid #007bff",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center py-3 px-4">
              <div className="d-flex align-items-center">
                <div>
                  <h1 style={{
                    fontSize: "1.8rem",
                    fontWeight: "600",
                    color: "#2c3e50",
                    margin: "0"
                  }}>
                    Seed Variety Management
                  </h1>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="contained"
                  onClick={() => setOpenAdd(true)}
                  style={{
                    backgroundColor: "#28a745",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "500",
                    textTransform: "none"
                  }}
                >
                  Add Seed Variety
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="mx-2" style={{ minHeight: "75vh", overflowY: "auto", overflowX: "auto", maxWidth: "100%", whiteSpace: "nowrap" }}>
          <table className="table table-hover mb-0" style={{
            minWidth: "800px",
            backgroundColor: "#fff"
          }}>
            <thead style={{
              backgroundColor: "#007bff",
              color: "white",
              position: "sticky",
              top: "0",
              zIndex: "10"
            }}>
              <tr>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "200px"
                }}>
                  Variety Name
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={varietyname}
                    onChange={(e) => {
                      setVarietyName(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "300px"
                }}>
                  Description
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  textAlign: "center",
                  minWidth: "150px"
                }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px" }}>
              {currentPageData.map((data, index) => (
                <tr key={index} style={{
                  backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                  borderBottom: "1px solid #dee2e6"
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e3f2fd";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f9fa" : "#ffffff";
                  }}>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6",
                    fontWeight: "500"
                  }}>
                    {data.varietyName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.description}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center"
                  }}>
                    <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleView(data)}
                        title="View"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleEdit(data)}
                        title="Edit"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaRegEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteClick(data)}
                        title="Delete"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='d-flex justify-content-between pb-0'>
          <div className='p-2'>
            Showing {startItem} to {endItem} of {totalItems} items
          </div>
          <div className='p-2'>
            <Pagination className="mb-0">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalpage} />
              <Pagination.Last onClick={() => handlePageChange(totalpage)} disabled={page === totalpage} />
            </Pagination>
          </div>
        </div>

        {/* View Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Seed Variety Management</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Variety Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.varietyName}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Description:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.description}</Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Seed Variety</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Variety Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={selectedRow.varietyName}
                  onChange={(e) => setSelectedRow({ ...selectedRow, varietyName: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Description"
                  type="text"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={selectedRow.description}
                  onChange={(e) => setSelectedRow({ ...selectedRow, description: e.target.value })}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit} color="primary">Cancel</Button>
            <Button onClick={handleConfEdit} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Dialog */}
        <Dialog open={openAdd} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Seed Variety</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Variety Name"
              fullWidth
              variant="outlined"
              value={newState.varietyName}
              onChange={(e) => setNewState({ ...newState, varietyName: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newState.description}
              onChange={(e) => setNewState({ ...newState, description: e.target.value })}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAdd} color="primary">Cancel</Button>
            <Button onClick={handleSaveAdd} color="primary">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete}>
          <DialogTitle>Delete Seed Variety</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this seed variety?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} color="primary">Cancel</Button>
            <Button onClick={handleDelete} color="primary">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Confirmation Dialog */}
        <Dialog open={openeditmodal} onClose={() => setOpenEditmodal(false)}>
          <DialogTitle>Edit Seed Variety</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to edit this seed variety?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditmodal(false)} color="primary">No</Button>
            <Button onClick={handleSaveEdit} color="primary">Yes</Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default Seedvariety;